import type React from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
  BackIcon,
  CloseIcon,
  HistoryIcon,
  HomeIcon,
  QRIcon,
  ScanIcon,
  SendIcon,
  StoreIcon,
} from '@components/icons/SvgIcons'

const ICON_MAP = {
  home: HomeIcon,
  store: StoreIcon,
  send: SendIcon,
  history: HistoryIcon,
  scan: ScanIcon,
  qr: QRIcon,
  back: BackIcon,
  close: CloseIcon,
} as const

export type NavigationIcon = keyof typeof ICON_MAP

export interface BottomNavigationItem {
  id: string
  icon: NavigationIcon
  label?: string
}

export interface BottomNavigationProps {
  items: BottomNavigationItem[]
  activeId?: string | null
  onSelect?: (id: string) => void
  className?: string
}

const containerClasses =
  'relative flex items-center justify-center gap-2 rounded-[56px] bg-white shadow-[0px_1px_1px_0.5px_rgba(24,24,27,0.05)] ring-1 ring-[#bfdbfe] ring-offset-[4px] ring-offset-[#eff6ff] p-1'

const baseItemClasses =
  'relative z-10 flex h-10 items-center justify-center rounded-[40px] px-4 transition-colors text-[#7b849b]'

const activeItemClasses = 'text-[#1f1f1f]'

const singleItemWidthClasses = 'w-12 px-2'

export const BottomNavigation: React.FC<BottomNavigationProps> = ({
  items,
  activeId,
  onSelect,
  className,
}) => {
  const firstId = useMemo(() => items[0]?.id, [items])
  const [internalActive, setInternalActive] = useState<string | undefined>(firstId)
  const isControlled = activeId !== undefined
  const resolvedActiveId = isControlled ? activeId : internalActive
  const containerRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const [highlight, setHighlight] = useState<{ width: number; left: number; visible: boolean }>({
    width: 0,
    left: 0,
    visible: false,
  })

  useEffect(() => {
    if (!isControlled) {
      setInternalActive(firstId)
    }
  }, [isControlled, firstId])

  const updateHighlight = useCallback(() => {
    const activeKey = resolvedActiveId
    const container = containerRef.current
    if (!container || !activeKey) {
      setHighlight((prev) => (prev.visible ? { width: 0, left: 0, visible: false } : prev))
      return
    }
    const activeEl = itemRefs.current.get(activeKey)
    if (!activeEl) {
      setHighlight((prev) => (prev.visible ? { width: 0, left: 0, visible: false } : prev))
      return
    }
    const containerRect = container.getBoundingClientRect()
    const activeRect = activeEl.getBoundingClientRect()
    setHighlight({
      width: activeRect.width,
      left: activeRect.left - containerRect.left,
      visible: true,
    })
  }, [resolvedActiveId])

  useLayoutEffect(() => {
    updateHighlight()
  }, [updateHighlight])

  useEffect(() => {
    const handleResize = () => updateHighlight()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [updateHighlight])

  const setItemRef = useCallback(
    (id: string, node: HTMLButtonElement | null) => {
      if (node) {
        itemRefs.current.set(id, node)
      } else {
        itemRefs.current.delete(id)
      }
      requestAnimationFrame(() => updateHighlight())
    },
    [updateHighlight]
  )

  const handleSelect = (id: string) => {
    if (!isControlled) {
      setInternalActive(id)
    }
    onSelect?.(id)
  }

  return (
    <div ref={containerRef} className={`${containerClasses} ${className ?? ''}`}>
      {highlight.visible ? (
        <div
          className="pointer-events-none absolute inset-y-1 rounded-[40px] bg-[#f0f0f1] transition-all duration-200 ease-out"
          style={{ width: highlight.width, left: highlight.left }}
        />
      ) : null}
      {items.map((item) => {
        const IconComponent = ICON_MAP[item.icon]
        const isActive = item.id === resolvedActiveId
        const widthClass = items.length === 1 ? singleItemWidthClasses : ''

        return (
          <button
            key={item.id}
            type="button"
            onClick={() => handleSelect(item.id)}
            ref={(node) => setItemRef(item.id, node)}
            className={`${baseItemClasses} ${widthClass} ${isActive ? activeItemClasses : ''}`}
            aria-pressed={isActive}
            aria-label={item.label ?? item.id}
          >
            <IconComponent decorative className="block size-6" />
          </button>
        )
      })}
    </div>
  )
}

export default BottomNavigation
